// src/hooks/useSettings.js
import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

function normalizeSettingsPayload(data) {
    // English comment: Accept either {items:[{key,value}]} or direct [{key,value}] payload.
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.items)) return data.items;
    return [];
}

export default function useSettings() {
    const [settings, setSettings] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const fetchSettings = useCallback(async () => {
        setLoading(true); setError("");
        try {
            const res = await axios.get(`${API_BASE}/api/settings`, { withCredentials: true, timeout: 30000 });
            const rows = normalizeSettingsPayload(res.data);
            setSettings(Array.isArray(rows) ? rows : []);
        } catch (e) {
            setError(e?.response?.data?.error || e?.message || "Konnte Settings nicht laden.");
        } finally {
            setLoading(false);
        }
    }, []);

    const upsertSetting = useCallback(async ({ key, value }) => {
        if (!key?.trim()) throw new Error("Key ist Pflichtfeld.");

        const res = await axios.post(
            `${API_BASE}/api/settings/${encodeURIComponent(key.trim())}`,
            { value },
            { withCredentials: true, timeout: 30000 }
        );

        if (res.status >= 400) throw new Error(res?.data?.error || "Speichern fehlgeschlagen.");

        const savedKey = res?.data?.key ?? key.trim();
        const savedValue = res?.data?.value ?? value;

        setSettings(prev => {
            const next = Array.isArray(prev) ? [...prev] : [];
            const idx = next.findIndex(r => r.key === savedKey);
            const row = { key: savedKey, value: savedValue };
            if (idx >= 0) next[idx] = row;
            else next.push(row);
            return next;
        });

        return res.data;
    }, []);

    const deleteSetting = useCallback(async (key) => {
        const k = (key || "").trim();
        if (!k) throw new Error("Key ist Pflichtfeld.");

        const res = await axios.delete(
            `${API_BASE}/api/settings/${encodeURIComponent(k)}`,
            { withCredentials: true, timeout: 30000 }
        );

        if (res.status >= 400) throw new Error(res?.data?.error || "Löschen fehlgeschlagen.");

        // English comment: Remove locally without refetch.
        setSettings(prev => (Array.isArray(prev) ? prev.filter(r => r.key !== k) : []));
        return res.data;
    }, []);

    useEffect(() => { fetchSettings(); }, [fetchSettings]);

    return { settings, loading, error, fetchSettings, upsertSetting, deleteSetting };
}
